import { FormEvent, useState } from "react";
import { TestIdClassNameProps } from "@app/types/Common";
import Button from "@components/AccessibleComponents/Button";
import TextInput from "@components/AccessibleComponents/TextInput";
import { EmarsysResponse } from "@components/Newsletter/types/Emarsys";
import { Newsletter } from "@components/Newsletter/types/Newsletter";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Transition } from "@headlessui/react";
import { newsletterRequests } from "@pages/Newsletter/SignUp/Newsletter.server";
import cx from "classnames";

export type ArticleNewsletterFormProps = TestIdClassNameProps & {
  newsletters: Newsletter[];
  showForm: boolean;
  emarsysResponse?: EmarsysResponse;
  setEmarsysResponse: (response: EmarsysResponse) => void;
};

export const ArticleNewsletterForm = ({
  testId,
  rootClassName,
  newsletters,
  showForm,
  emarsysResponse,
  setEmarsysResponse,
}: ArticleNewsletterFormProps) => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    const target = event.target as typeof event.target & {
      email: { value: string };
    };

    const request = async () => {
      await newsletterRequests
        .processSubscription(target.email.value, newsletters)
        .then((response) => {
          setEmarsysResponse(response);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    request();
  };

  return (
    <Transition.Root
      show={showForm}
      as="form"
      onSubmit={handleSubmit}
      className={cx(rootClassName, "grid grid-cols-6 gap-x-3")}
      data-testid={testId}
    >
      <Transition.Child
        className="col-span-3 sm:col-span-4"
        enter="transition ease-in-out duration-300 transform"
        enterFrom="translate-x-full opacity-0"
        enterTo="translate-x-0 opacity-100"
      >
        <TextInput
          className={cx(
            "h-full w-full py-1.5 font-public-sans text-base font-normal placeholder-gray-600 outline-none"
          )}
          name="email"
          type="email"
          placeholder="Your email address"
          required
        />
      </Transition.Child>

      <Transition.Child
        className="col-span-3 sm:col-span-2"
        enter="transition ease-in-out duration-300 transform"
        enterFrom="-translate-x-full opacity-0"
        enterTo="translate-x-0 opacity-100"
      >
        <Button
          type="submit"
          className={cx(
            "w-full cursor-pointer whitespace-nowrap rounded-full bg-gray-850 px-6 py-2 text-center text-base text-white",
            "hover:bg-gray-675"
          )}
        >
          {loading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin={loading}
              size="lg"
              color="text-gray-850"
            />
          ) : (
            "Sign up"
          )}
        </Button>
      </Transition.Child>

      <div
        role="alert"
        className={cx(
          "relative col-span-6 mt-1 border-t font-poppins text-xs font-semibold text-red",
          emarsysResponse?.status === "error"
            ? "border-red pt-1"
            : "border-gray-850"
        )}
      >
        {emarsysResponse?.status === "error" &&
        emarsysResponse.emarsysCode === 5
          ? "E-mail field is required."
          : emarsysResponse?.message}
      </div>
    </Transition.Root>
  );
};
